import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useBrowseRowContext } from '../BrowseContext';
import type { BrowseFollowKind } from '../hooks/useBrowseFollow';
import { browseMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseFollowButtonProps {
  kind: BrowseFollowKind;
  id: string;
  name: string;
  followed: boolean;
  /** The phone row (design c1): a round 40 px button with the icon only. */
  iconOnly?: boolean;
}

/**
 * Follow on every row (design a1). A followed row says "Following" and unfollows on a click. A full
 * follow slot shows "5 of 5" with a lock and opens the plan prompt (design b7). A playlist says
 * "Track playlist" / "Tracking".
 */
const BrowseFollowButtonComponent: React.FC<BrowseFollowButtonProps> = ({ kind, id, name, followed, iconOnly }) => {
  const intl = useIntl();
  const { follow, unfollow, pendingId, slotsFull, usage } = useBrowseRowContext();
  const openUpgrade = useUpgradePrompt();
  const full = !followed && slotsFull(kind);

  const onClick = useCallback(() => {
    if (full) {
      openUpgrade(SMLUpgradeFeature.followLimit);
      return;
    }
    (followed ? unfollow(kind, id) : follow(kind, id)).catch(console.error);
  }, [full, followed, follow, unfollow, kind, id, openUpgrade]);

  let label: string;
  if (full && usage !== undefined) {
    const slots = kind === 'artist' ? usage.followSlots.artists : usage.followSlots.tracks;
    label = intl.formatMessage(messages.slotsFull, { used: slots.used, limit: slots.limit });
  } else if (kind === 'playlist') {
    label = intl.formatMessage(followed ? messages.tracking : messages.trackPlaylist);
  } else {
    label = intl.formatMessage(followed ? messages.following : messages.follow);
  }

  let icon = faPlus;
  if (full) {
    icon = faLock;
  } else if (followed) {
    icon = faCheck;
  }

  return (
    <button
      type="button"
      className={clsx(
        styles.followBtn,
        followed && styles.followBtnOn,
        full && styles.followBtnFull,
        iconOnly === true && styles.followBtnIcon
      )}
      onClick={onClick}
      disabled={pendingId === id}
      aria-pressed={followed}
      aria-label={intl.formatMessage(followed ? messages.unfollowLabel : messages.followLabel, { name })}
      title={iconOnly === true ? label : undefined}
    >
      <FontAwesomeIcon icon={icon} />
      {iconOnly !== true && label}
    </button>
  );
};

export const BrowseFollowButton = TypedMemo(BrowseFollowButtonComponent);
